import { getCountry } from '@iwIntlProvider/CountryProvider';
import { UKOnly } from '@iwIntlProvider/CountryWrappers';
import { fetchPostManualRepaymentIntent } from '@iwoca/lapi-client/edge';
import { Text } from '@iwoca/orion-ui';
import { RootState } from '@iwProvider/configureStore';
import { useMutation } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import styles from './ManualPaymentModal.module.css';
import { FormattedCurrency } from '../../../iwIntlProvider/FormattedCurrency';
import { CLOSE_MODAL } from '../../../iwModals/Modals.actions';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { REQUEST_CHANGE_ROUTER } from '../../../iwRouter/IwRouter.actions';
import { FLEXI_12_URL } from '../../dashboard/Dashboard.sagas';
import ConfirmationButton from '../../ui/confirmationButton/ConfirmationButton';
import { Prompt } from '../../ui/prompt';

const mapStateToProps = ({ manualPaymentModal }: RootState) =>
    manualPaymentModal;

const mapDispatchToProps = (dispatch: Dispatch) => ({
    cancel: () => dispatch({ type: CLOSE_MODAL }),
    goToDashboard: () =>
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            location: { pathname: FLEXI_12_URL }
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const ManualPaymentModal = ({
    amount,
    iban,
    accountNumber,
    sortCode,
    name,
    reference,
    accountId,
    cancel,
    goToDashboard
}: PropsFromRedux) => {
    const formattedManualPaymentAmount = <FormattedCurrency value={amount} />;
    const stateKey = useStateKey();

    const manualRepaymentIntentMutation = useMutation({
        mutationFn: () =>
            fetchPostManualRepaymentIntent({
                stateKey: stateKey!,
                accountId,
                body: {
                    data: {
                        amount
                    }
                }
            }),
        onSettled: () => goToDashboard()
    });

    const handleSentMoney = () => {
        if (getCountry() === 'DE') {
            manualRepaymentIntentMutation.mutate();
        } else {
            goToDashboard();
        }
    };

    return (
        <div className={styles.BankTransferModal}>
            <div className={styles.title}>
                <FormattedMessage
                    id="_.modal.manual_payment.title"
                    defaultMessage="Make an early repayment"
                />
            </div>

            <div className={styles.subTitle}>
                <FormattedMessage
                    id="_.modal.manual_payment.sub_title"
                    defaultMessage="Make an early repayment of {amount} via bank transfer using the following account details."
                    values={{
                        amount: formattedManualPaymentAmount
                    }}
                />
            </div>

            <div className={styles.warningMessage}>
                <FormattedMessage
                    id="_.modal.manual_payment.warning_message"
                    defaultMessage="Please ensure you use the correct reference number."
                />
            </div>

            <div className={styles.content}>
                <table>
                    <tbody>
                        {iban ? (
                            <>
                                <tr>
                                    <td>
                                        <FormattedMessage
                                            id="_.modal.manual_payment.iban"
                                            defaultMessage="IBAN"
                                        />
                                    </td>
                                    <td>{iban}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <FormattedMessage
                                            id="_.modal.manual_payment.reference_name"
                                            defaultMessage="Name"
                                        />
                                    </td>
                                    <td>{reference}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <FormattedMessage
                                            id="_.your_company.repayment_card.payee"
                                            defaultMessage="Payee"
                                        />
                                    </td>
                                    <td>{name}</td>
                                </tr>
                            </>
                        ) : (
                            <>
                                <tr>
                                    <td>
                                        <FormattedMessage
                                            id="_.modal.manual_payment.account_nunmber"
                                            defaultMessage="Account number"
                                        />
                                    </td>
                                    <td>{accountNumber}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <FormattedMessage
                                            id="_.modal.manual_payment.sort_code"
                                            defaultMessage="Sort code"
                                        />
                                    </td>
                                    <td>{sortCode}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <FormattedMessage
                                            id="_.modal.manual_payment.reference_name"
                                            defaultMessage="Name"
                                        />
                                    </td>
                                    <td>{name}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <FormattedMessage
                                            id="_.modal.manual_payment.reference_id"
                                            defaultMessage="Reference"
                                        />
                                    </td>
                                    <td>{reference}</td>
                                </tr>
                            </>
                        )}
                    </tbody>
                </table>
                <UKOnly>
                    <Text className="mt-s text-s font-bold">
                        <FormattedMessage
                            id="_.modal.manual_payment.chaps_repayments"
                            defaultMessage="CHAPS payments will not be accepted to this account. Please contact your Account Manager for alternative details."
                        />
                    </Text>
                </UKOnly>
            </div>

            <Prompt className={styles.prompt}>
                <button onClick={cancel}>
                    <FormattedMessage
                        id="_.modal.manual_payment.cancel"
                        defaultMessage="Cancel"
                    />
                </button>

                <ConfirmationButton
                    onClick={handleSentMoney}
                    disabled={manualRepaymentIntentMutation.isLoading}
                >
                    <FormattedMessage
                        id="_.modal.manual_payment.sent_money"
                        defaultMessage="I've sent the money"
                    />
                </ConfirmationButton>
            </Prompt>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(ManualPaymentModal);
